import React from 'react';

import { useIntl } from 'react-intl';

import { cssMap, cx } from '@atlaskit/css';
import PlanIcon from '@atlaskit/icon-lab/core/plan';
import Lozenge from '@atlaskit/lozenge';
import { Box, Inline, Pressable, Text } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';
import Tooltip from '@atlaskit/tooltip';

import messages from './messages.ts';

const styles = cssMap({
	wrapper: {
		border: `${token('border.width')} solid ${token('color.border')}`,
		borderRadius: token('radius.small'),
		paddingRight: token('space.050'),
		paddingLeft: token('space.050'),
		paddingBlock: token('space.025'),
		width: 'fit-content',
	},
	inactiveWrapper: {
		opacity: token('opacity.disabled'),
	},
	pressable: {
		borderRadius: token('radius.small'),
		padding: '0',
		backgroundColor: token('color.background.neutral.subtle'),
		'&:hover': {
			backgroundColor: token('color.background.neutral.subtle.hovered'),
		},
	},
});

type ScenarioPillProps = {
	name: string;
	isActive: boolean;
	isDefault: boolean;
	onClick?: () => void;
};

export const ScenarioPill = ({
	name,
	isActive,
	isDefault,
	onClick,
}: ScenarioPillProps): React.JSX.Element => {
	const { formatMessage } = useIntl();

	const renderContent = () => {
		return (
			<Tooltip
				canAppear={() => !isActive}
				content={formatMessage(messages.inactiveScenarioTooltip)}
				position="right"
			>
				{(tooltipProps) => (
					<Box {...tooltipProps} xcss={cx(styles.wrapper, !isActive && styles.inactiveWrapper)}>
						<Inline alignBlock="center" space="space.050" shouldWrap>
							<PlanIcon label="" />
							<Text>{name}</Text>
							{isDefault ? <Lozenge>{formatMessage(messages.defaultScenarioTag)}</Lozenge> : null}
						</Inline>
					</Box>
				)}
			</Tooltip>
		);
	};

	if (onClick) {
		return (
			<Pressable onClick={onClick} xcss={styles.pressable}>
				{renderContent()}
			</Pressable>
		);
	}

	return renderContent();
};
